import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { EChartsOption } from "echarts";


interface sensorItem {
  id: string;
  name: string;
  critical: number;
  cautious: number;
  warning: number;
  riverData: any;
  riverName: string;
  fullScreen: boolean;
  lastUpdate: string | null;
  showDialog: boolean;
  dimensions: {
    resizableBoxwidth: number;
    resizableBoxheight: number;
  };
  chartWidth: number;
  chartHeight: number;

}

const RiverSensorChart: React.FC<sensorItem> = ({
  id,
  name,
  critical,
  cautious,
  warning,
  riverData,
  riverName,
  fullScreen,
  lastUpdate,
  showDialog,
  dimensions,
  chartWidth,
  chartHeight,

}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartInstance, setChartInstance] = useState<echarts.ECharts>();
  const [secondDataSetReceived, setSecondDataSetReceived] =
    useState<boolean>(false);
  const [legendData, setLegendData] = useState<string[]>([name]);
  const [graphColors, setGraphColors] = useState<
    echarts.graphic.LinearGradient[]
  >(() => generateRandomGradientColors(10));
  const [initialData, setInitialData] = useState<any[]>([]);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  useEffect(() => {
    const fetchDataAndInitializeChart = async () => {
      const RIVER_LEVEL_GRAPH_URL = process.env.REACT_APP_RIVER_LEVEL_GRAPH_URL;
      try {
        const response = await fetch(
          `${RIVER_LEVEL_GRAPH_URL}` +
            id
        );
        const responseData = await response.json();

        if (responseData && Array.isArray(responseData)) {
          
          const data = responseData.map((item: any) => [
            item.timestamp,
            item.value,
          ]);

          setInitialData(data);
        } else {
          console.error("Invalid response data format:", responseData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchDataAndInitializeChart();
  }, [id]);

  useEffect(() => {
    if (
      chartRef.current &&
      chartRef.current.clientWidth &&
      chartRef.current.clientHeight
    ) {
      const chart = echarts.init(chartRef.current);

      const chartOption: EChartsOption = {
        color: ["#80FFA5", "#00DDFF", "#37A2FF", "#FF0087", "#FFBF00"],
        title: {
          left: "center",
          text: name,
          textStyle: {fontSize:"0.8rem",
            color: "#FFFFFF",
            
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
            label: {
              backgroundColor: "#6a7985",
            },
          },
        },
        legend: {
          data: [name, riverName],
          textStyle: {
            color: "#FFFFFF",
          },
          right: "10%",
          top: "10%",
          backgroundColor: 'rgba(0,0,0,0.5)',
          padding: [5, 10],
          borderRadius: 4,
          itemGap: 10,
          itemWidth: 20,
          itemHeight: 10
        },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: "none",
            },
            restore: {},
            saveAsImage: {},
          },
        },
        grid: {
          left: "3%",
          right: "14%",
          bottom: "20%",
          containLabel: true,
        },
        xAxis: {
          type: "time",
          name: "Time",
          axisLabel: {
            color: "#FFFFFF",
          },
          // boundaryGap: [0, "100%"],
          axisLine: {
            lineStyle: {
              color: "#FFFFFF",
              width: 2,
            },
          },
        },
        yAxis: {
          type: "value",
          name: "Water Level",
          nameTextStyle: {
            color: "#FFFFFF",
          },
          axisLabel: {
            formatter: "{value} m",
            color: "#FFFFFF",
          },
          boundaryGap: [0, "100%"],
          axisLine: {
            lineStyle: {
              color: "#070807",
              width: 2,
            },
          },
          splitLine: {
            lineStyle: {
              color: "rgba(85, 84, 95, 0.6)",
              width: 1,
            },
          },
          // ...splitLineStyle,
        },

        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 100,
          },
          {
            start: 0,
            end: 100,
          },
        ],
        visualMap: {
          type: "piecewise",
          show: false,
          dimension: 1,
          pieces: [
            {
              gt: 0,
              lt: cautious,
              color: "rgba(105,125,194,0.9)",
            },
            {
              gt: cautious,
              lt: warning,
              color: "#ffff00",
            },
            {
              gt: warning,
              lt: critical,
              color: "#ff8c00",
            },
            {
              gt: critical,
              color: "#f00e0e",
            },
          ],
        },
        series: [
          {
            name: name,
            type: "line",
            stack: "Total",
            smooth: true,
            symbol: "diamond",

                   lineStyle: {
                          width: 2,
                          color: "rgb(5, 255, 255)",
                        },
                        itemStyle: {
                          color: '#00DDFF'
                        },
                        areaStyle: {
                          opacity: 0.8,
                          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                              offset: 0,
                              color: "rgb(0, 225, 255, 0.2)",
                            },
                            {
                              offset: 1,
                              color: "rgba(54, 180, 253, 0.78)",
                            },
                          ]),
                        },
            emphasis: {
              focus: "series",
            },
            data: initialData,
            markLine: {
              symbol: "circle",
              data: [
                {
                  name: "Danger",
                  yAxis: critical,
                  itemStyle: {
                    color: "red",
                  },
                  label: {
                    formatter: " Danger Level",
                  },
                },
                {
                  name: "Warning",
                  yAxis: warning,
                  itemStyle: {
                    color: "orange",
                  },
                  label: {
                    formatter: " Warning Level",
                  },
                },
                {
                  name: "Cautious",
                  yAxis: cautious,
                  itemStyle: {
                    color: "yellow",
                  },
                  label: {
                    formatter: " Cautious Level",
                  },
                },
              ],
            },
          },
        ],
      };

      chart.setOption(chartOption);
      setChartInstance(chart);
    }

    return () => {
      if (chartInstance) {
        chartInstance.dispose();
      }
    };
  }, [initialData]);

  const generateNewData = async () => {
    const RIVER_LEVEL_THREE_DAYS_URL = process.env.REACT_APP_RIVER_LEVEL_THREE_DAYS_URL;
    try {
      const response = await fetch(
        `${RIVER_LEVEL_THREE_DAYS_URL}` +
          id
      );
      const responseData = await response.json();

      if (responseData && Array.isArray(responseData.results)) {
        const newData = responseData.results.map((item: any) => [
          item.timestamp,
          item.value,
        ]);
        setInitialData((prevData) => [...prevData, ...newData]);
      } else {
        console.error("Invalid response data format:", responseData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (showDialog) {
      const interval = setInterval(() => {
        generateNewData();
      }, 15 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [showDialog]);

  useEffect(() => {
    const updateChartSeries = () => {
      if (chartInstance && riverData && riverData.length > 0) {
        const option = chartInstance.getOption() as echarts.EChartsOption;

        if (!Array.isArray(option.series)) {
          option.series = [];
        }

        option.title = {
          left: "center",
          text: "",
          textStyle: {
            color: "#FFFFFF",
          },
        };

        option.series = option.series.concat({
          name: riverName,
          type: "line",
          smooth: true,
          symbol: "diamond",
          data: riverData,
          lineStyle: {
            width: 2,
            color:  graphColors[option.series.length % graphColors.length]
          },
          itemStyle: {
            color:  graphColors[option.series.length % graphColors.length],
          },
          areaStyle: {
            opacity: 0.8,
            color: graphColors[option.series.length % graphColors.length],
          },
          emphasis: {
            focus: "series",
          },
        });

        setLegendData((prevLegendData) => {
          const newLegendData = [...prevLegendData, riverName];
          option.legend = {
            ...option.legend,
            data: newLegendData,
            textStyle: {
              color: "#FFFFFF",
            },
            right: "10%",
            top: "10%",
            backgroundColor: 'rgba(0,0,0,0.5)',
            padding: [5, 10],
            borderRadius: 4,
            itemGap: 10,
            itemWidth: 20,
            itemHeight: 10
          };
          return newLegendData;
        });

        chartInstance.setOption(option);
      }
    };
    updateChartSeries();
  }, [riverData, chartInstance, secondDataSetReceived]);

  useEffect(() => {
    if (chartInstance) {
      setTimeout(() => {
        chartInstance.resize();
      }, 100);
    }
  }, [fullScreen]);

  useEffect(() => {
    const handleResize = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [chartInstance]);

  useEffect(() => {

    if(chartWidth !== 0){
      setInitialLoad(false);
    }
  }, [chartWidth,initialLoad]);

  useEffect(() => {
    if (chartInstance) {
      chartInstance.resize();
    }
  }, [chartWidth,chartHeight]); 

  return (
    <div
      id="main"
      style={{ width:initialLoad?"100%" :`${chartWidth}px`, height:initialLoad?"40vh" :`${chartHeight}px `}} 
      ref={chartRef}
    ></div>
  );
};

export default RiverSensorChart;

function generateRandomGradientColors(
  count: number
): echarts.graphic.LinearGradient[] {
  const colors: echarts.graphic.LinearGradient[] = [];

  // Define your custom colors
  const customColors = [
    ["#FF5733", "#FFBD33"], // Gradient 1
    ["#33FF57", "#33FFBD"], // Gradient 2
    ["#5733FF", "#BD33FF"], // Gradient 3
    ["#33BDFF", "#3357FF"], // Gradient 4
    ["#FF33BD", "#FF3357"], // Gradient 5
  ];

  for (let i = 0; i < count; i++) {
    let startColor, endColor;
    
    // Use custom colors for the first 5 iterations
    if (i < customColors.length) {
      startColor = customColors[i][0];
      endColor = customColors[i][1];
    } else {
      // Generate random colors after the custom ones are used
      startColor = getRandomColor();
      endColor = getRandomColor();
    }

    // Create the gradient and push it to the array
    colors.push(
      new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: startColor },
        { offset: 1, color: endColor },
      ])
    );
  }

  return colors;
}

function getRandomColor(): string {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}
